import React, { useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { StringParam, useQueryParam } from 'use-query-params';
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel
} from '@material-ui/core';
import SchoolIcon from '@material-ui/icons/School';

import CardTitle from '../../../common/card/CardTitle';
import StatusLabel from '../../../common/StatusLabel';
import { formatDate } from '../../../common/formatter';

import { useGetUserTrainings } from '../../../user/User.hooks';
import { UserContext } from '../../../user/User.context';
import { TrainingUser } from '../Training.interface';

export interface TrainingLogCardProps {
  userId?: number;
  compact?: boolean;
  isViewOnly?: boolean;
}

const TrainingLogCard: React.FC<TrainingLogCardProps> = ({
  userId,
  compact = false,
  isViewOnly
}) => {
  const history = useHistory();
  const { user } = useContext(UserContext);
  const { userTrainings, get: getUserTrainings } = useGetUserTrainings(
    false,
    userId || user?.id
  );
  const [order, setOrder] = useQueryParam('order', StringParam);
  const [orderDirection, setOrderDirection] = useQueryParam(
    'direction',
    StringParam
  );

  useEffect(() => {
    const params = {
      order: String(order || '')
    } as any;
    if (order && orderDirection) {
      params.order = `${order} ${orderDirection}`;
    }
    getUserTrainings(params);
  }, [order, orderDirection, getUserTrainings]);

  const handleViewTrainingLog = (userTraining: TrainingUser) => {
    let path = `/portal/training/${userTraining.trainingVersion.trainingId}/version/${userTraining.trainingVersion.id}/user/${userTraining.userId}`;
    if (userId) path = path + '?view-only=1';
    history.push(path);
  };

  const handleSort = (column: string) => {
    setOrder(column);
    setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && orderDirection === 'DESC' ? 'desc' : 'asc';
  };

  return (
    <Card>
      <CardTitle>
        <SchoolIcon />
        Training Log
      </CardTitle>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={order === 'id'}
                  direction={getSortDirection('id')}
                  onClick={() => handleSort('id')}
                >
                  Training ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'name'}
                  direction={getSortDirection('name')}
                  onClick={() => handleSort('name')}
                >
                  Training Name
                </TableSortLabel>
              </TableCell>
              <TableCell>Document ID</TableCell>
              <TableCell>Document Version</TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'status'}
                  direction={getSortDirection('status')}
                  onClick={() => handleSort('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              {!compact && (
                <>
                  <TableCell>
                    <TableSortLabel
                      active={order === 'version'}
                      direction={getSortDirection('version')}
                      onClick={() => handleSort('version')}
                    >
                      Training Plan Version
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={order === 'assigned_date'}
                      direction={getSortDirection('assigned_date')}
                      onClick={() => handleSort('assigned_date')}
                    >
                      Date Assigned
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={order === 'due_date'}
                      direction={getSortDirection('due_date')}
                      onClick={() => handleSort('due_date')}
                    >
                      Due Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={order === 'completed_date'}
                      direction={getSortDirection('completed_date')}
                      onClick={() => handleSort('completed_date')}
                    >
                      Date Completed
                    </TableSortLabel>
                  </TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {userTrainings?.length === 0 && (
              <TableRow>
                <TableCell>There are no records</TableCell>
              </TableRow>
            )}
            {userTrainings?.map((userTraining: TrainingUser) => {
              const document = userTraining.trainingVersion.fileMetadata?.[0];
              return (
                <TableRow
                  hover
                  key={userTraining.id}
                  onClick={() => handleViewTrainingLog(userTraining)}
                >
                  <TableCell>
                    TRAINING-{userTraining.trainingVersion.trainingId}
                  </TableCell>
                  <TableCell>{userTraining.trainingVersion.name}</TableCell>
                  <TableCell>{document?.taskKey || 'N/A'}</TableCell>
                  <TableCell>{document?.version || 'N/A'}</TableCell>
                  <TableCell>
                    <StatusLabel status={userTraining.status} />
                  </TableCell>
                  {!compact && (
                    <>
                      <TableCell>
                        {userTraining.trainingVersion.version}
                      </TableCell>
                      <TableCell>
                        {formatDate(userTraining.assignedDate)}
                      </TableCell>
                      <TableCell>{formatDate(userTraining.dueDate)}</TableCell>
                      <TableCell>
                        {formatDate(userTraining.completedDate)}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

export default TrainingLogCard;
